import { useState, useEffect, useRef, type ReactNode } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'

import { api } from '~/api'
import Connections from './connections'
import Logs from './logs'
import WalmartGLAccounts from './walmart_gl_accounts'
import ShopifyGLAccounts from './shopify_gl_accounts'
import WalmartLocations from './walmart_locations'
import ShopifyLocations, { ShopifyLocationsHandle } from './shopify_locations'
import RainforestItems, { RainforestItemsHandle } from './items'
import WalmartReturns from './walmart_returns'
import { apiClient } from '~/api'

export type RainforestEnv = 'sandbox' | 'production'

export function RainforestSettings({ env = 'production' as RainforestEnv }) {
    const [currentTab, setCurrentTab] = useState(0)

    const { data, error, status } = api.integrations.rainforest.settingsByName.useQuery(
        ['settings', { name: env }],
        { params: { name: env } }
    )

    const [settings, setSettings] = useState<Record<string, unknown>>({})

    useEffect(() => {
        if (status === 'success') {
            setSettings(data?.body.settings ?? {})
        }
    }, [status, data])

    const shops = data?.body.shops ?? []
    const subsidiaries = data?.body.subsidiaries ?? []
    const customers = data?.body.customers ?? []
    const locations = data?.body.locations ?? []
    const items = data?.body.items ?? []
    const accounts = data?.body.accounts ?? []
    // Best-effort extraction of log history from integration data payload
    const logHistory: Array<{ timestamp: number; module?: string; log_message?: string }> =
        (data?.body.log_history as Array<{ timestamp: number; connection?: string; module?: string; log_message?: string }>) ?? []

    const updateMutation = api.integrations.rainforest.updateSettingsByName.useMutation()
    const [saved, setSaved] = useState(false)

    const itemsRef = useRef<RainforestItemsHandle>(null)
    const shopLocRef = useRef<ShopifyLocationsHandle>(null)

    const handleSave = async () => {
        // Validate item rows first; abort save if any validation errors
        if (itemsRef.current && !itemsRef.current.validate()) {
            setCurrentTab(TAB_DEFINITIONS.findIndex(t => t.label === 'Items'));
            return;
        }
        // Validate shopify locations
        if (shopLocRef.current && !shopLocRef.current.validate()) {
            setCurrentTab(TAB_DEFINITIONS.findIndex(t => t.label === 'Shopify locations'));
            return;
        }
        if (updateMutation.status === 'loading') return
        try {
            setSaved(false)
                    // After validation passes, save dirty rows
        const dirtyRows = itemsRef.current?.getDirtyRows() ?? []
        if (dirtyRows.length) {
            await apiClient.post(`integrations/rainforest/${env}/items/`, { json: dirtyRows }).json()
            itemsRef.current?.resetDirty()
        }

        // Save dirty shopify location rows
        const locRows = shopLocRef.current?.getDirtyRows() ?? []
        if (locRows.length) {
            await apiClient.post(`integrations/rainforest/${env}/locations/`, { json: locRows }).json()
            shopLocRef.current?.resetDirty()
        }

        await updateMutation.mutateAsync({ params: { name: env }, body: { settings } })
            setSaved(true)
        } catch (err) {
            console.error(err)
        }
    }

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue)
    }

    const TAB_DEFINITIONS: Array<{
        label: string
        node: ReactNode
    }> = [
        { label: 'Connections', node: <Connections settings={settings} shops={shops} subsidiaries={subsidiaries} customers={customers} accounts={accounts} setSettings={setSettings} /> },
        { label: 'Walmart GL Accounts', node: <WalmartGLAccounts settings={settings} shops={shops} accounts={accounts} setSettings={setSettings} /> },
        { label: 'Shopify GL Accounts', node: <ShopifyGLAccounts settings={settings} shops={shops} accounts={accounts} setSettings={setSettings} /> },
        { label: 'Walmart locations', node: <WalmartLocations settings={settings} shops={shops} locations={locations} setSettings={setSettings} /> },
        { label: 'Shopify locations', node: <ShopifyLocations ref={shopLocRef} env={env} shops={shops} settings={settings} locations={locations} /> },
        { label: 'Items', node: <RainforestItems ref={itemsRef} env={env} shops={shops} settings={settings} items={items} /> },
        { label: 'Walmart returns', node: <WalmartReturns env={env} /> },
        { label: 'Logs', node: <Logs env={env} logs={logHistory} /> },
    ]

    return (
        <Paper sx={{ minWidth: 1200, margin: '0 auto', mt: 2.5, p: 2.5, width: '60%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={currentTab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    textColor="secondary"
                    indicatorColor="secondary"
                    sx={{ flexGrow: 1 }}
                >
                {TAB_DEFINITIONS.map((tab, index) => (
                    <Tab key={tab.label} label={tab.label} value={index} />
                ))}
            </Tabs>
                {saved && (
                    <Typography sx={{ color: 'text.secondary', mr: 2 }}>Saved</Typography>
                )}
                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    disabled={updateMutation.status === 'loading'}
                    onClick={handleSave}
                >
                    {updateMutation.status === 'loading' ? (
                        <CircularProgress size={16} color="inherit" />
                    ) : (
                        'Save'
                    )}
                </Button>
            </Stack>

            <Box sx={{ mt: 3 }}>
                {status === 'loading' && <Skeleton variant="rectangular" height={120} />}
                {status === 'error' && (
                    <Alert severity="error">{(error as Error).message}</Alert>
                )}
                {status === 'success' && TAB_DEFINITIONS[currentTab].node}
                {status === 'loading' && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
            </Box>
        </Paper>
    )
}

export default RainforestSettings;

// Convenience exports for explicit environments
export function RainforestSettingsProduction() {
    return <RainforestSettings env="production" />
}

export function RainforestSettingsSandbox() {
    return <RainforestSettings env="sandbox" />
}
